import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { analyticsService, useComponentImpression } from '@meiuca/analytics'
import { ANALYTICS_EVENTS, SCREEN_NAMES, COMPONENT_TYPES } from '@/constants/analyticsEvents'
import { H4, Body, Caption } from '@meiuca/ui-components'
import { formatDateForUI, parseAPIDate } from '@meiuca/web-foundation'
import { CalendarSync, ChevronRight, Loader2 } from 'lucide-react'
import { MarketplaceHeader } from '@/components/MarketplaceHeader'
import { useAuth } from '@/contexts/AuthContext'
import { LoginRequiredEmptyState } from '@/components/LoginRequiredEmptyState'
import { makeupService, type MakeupTrainingCenterGroup } from '@/services/makeupService'

function creditsLabel(count: number): string {
  return count === 1 ? '1 crédito disponível' : `${count} créditos disponíveis`
}

function MakeupTrainingCenterCard({
  group,
  position,
  onClick
}: {
  group: MakeupTrainingCenterGroup
  position: number
  onClick: () => void
}) {
  const impressionRef = useComponentImpression({
    componentType: COMPONENT_TYPES.MAKEUP_TRAINING_CENTER_CARD,
    componentKey: `makeup_tc_${group.trainingCenterId}`,
    position
  })

  return (
    <div
      ref={impressionRef}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      role="button"
      tabIndex={0}
      className="rounded-lg bg-background-white border border-border-light p-4 transition-shadow hover:shadow-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <Body className="text-base font-semibold text-text-primary mb-1">
            {group.trainingCenterName}
          </Body>
          <Caption className="text-text-secondary">{creditsLabel(group.count)}</Caption>
          {group.nextExpiresAt && (
            <Caption className="text-text-secondary block mt-1">
              Próxima validade: {formatDateForUI(parseAPIDate(group.nextExpiresAt))}
            </Caption>
          )}
        </div>
        <div className="flex items-center gap-2">
          {group.expiringSoonCount > 0 && (
            <span className="px-2 py-1 rounded text-xs font-medium bg-feedback-warning/15 text-feedback-warning whitespace-nowrap">
              {group.expiringSoonCount === 1
                ? '1 expira em breve'
                : `${group.expiringSoonCount} expiram em breve`}
            </span>
          )}
          <ChevronRight className="w-4 h-4 text-text-tertiary flex-shrink-0" />
        </div>
      </div>
    </div>
  )
}

export function MakeupsPage() {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth()

  const [groups, setGroups] = useState<MakeupTrainingCenterGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    analyticsService.trackScreenView(SCREEN_NAMES.MAKEUPS)
  }, [])

  useEffect(() => {
    // Show the login empty state when logged out instead of redirecting away.
    if (isAuthenticated) {
      loadGroups()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  const loadGroups = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await makeupService.listCreditsGroupedByTrainingCenter()
      setGroups(data)
    } catch (err) {
      console.error('Error loading makeup credits:', err)
      setError('Erro ao carregar seus créditos. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  const openAgenda = (group: MakeupTrainingCenterGroup, position: number) => {
    analyticsService.track(ANALYTICS_EVENTS.MAKEUPS_TRAINING_CENTER_CARD_CLICKED, {
      ct_id: group.trainingCenterId,
      credit_count: group.count,
      position
    })
    navigate(`/reposicoes/${group.trainingCenterId}`, {
      state: {
        trainingCenterName: group.trainingCenterName,
        creditCount: group.count
      }
    })
  }

  return (
    <div className="min-h-screen bg-background-primary">
      <MarketplaceHeader />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-6">
          <H4 className="text-text-primary font-semibold mb-2">Meus créditos</H4>
          <Body className="text-text-secondary">
            Use seus créditos — de reposição ou de pacote — para se inscrever em aulas
          </Body>
        </div>

        {isAuthLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-10 h-10 text-brand-primary animate-spin" />
          </div>
        ) : !isAuthenticated ? (
          <LoginRequiredEmptyState
            title="Entre para ver seus créditos"
            description="Faça login para consultar seus créditos e agendar uma nova aula."
          />
        ) : loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-10 h-10 text-brand-primary animate-spin" />
            <Body className="text-text-secondary mt-4">Carregando créditos...</Body>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Body className="text-feedback-error mb-4">{error}</Body>
            <button
              onClick={loadGroups}
              className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90 transition-colors"
            >
              Tentar novamente
            </button>
          </div>
        ) : groups.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 mb-4 rounded-full bg-background-secondary flex items-center justify-center">
              <CalendarSync className="w-8 h-8 text-text-tertiary" />
            </div>
            <H4 className="text-text-primary mb-2">Nenhum crédito disponível</H4>
            <Body className="text-text-secondary text-center max-w-md">
              Quando você cancelar uma aula com direito a reposição — ou comprar um pacote de aulas — os créditos aparecem aqui.
            </Body>
          </div>
        ) : (
          <div className="space-y-4">
            {groups.map((group, index) => (
              <MakeupTrainingCenterCard
                key={group.trainingCenterId}
                group={group}
                position={index}
                onClick={() => openAgenda(group, index)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
